import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import { treatInvoice } from 'shared/serializer';
import type { Invoice } from 'types/store/invoice-state';
import { InvoiceResponseSchema } from './invoice-schemas';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useLegacyAxiosInstance,
} from './utils';

export type { InvoiceResponse } from './invoice-schemas';

export async function loadInvoiceFn(axiosInstance: Promise<AxiosInstance>, id: number) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`/invoices/${id}`);

  const parseResult = InvoiceResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'invoice');
    throw new SilentError();
  }

  return treatInvoice<Invoice>(parseResult.data.data as unknown as Invoice);
}

export function useLoadInvoice(id: number | undefined, options: QueryOptions<Invoice> = {}) {
  const axiosInstance = useLegacyAxiosInstance();

  const queryResult = useQuery<Invoice, ApiError>({
    queryKey: ['invoices', id],
    queryFn: () => loadInvoiceFn(axiosInstance, id!),
    enabled: isDefined(id),
    ...options,
  });

  return addResourceNameToQueryResult<Invoice, ApiError, 'invoice'>('invoice', queryResult);
}
